import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { ChallengeState, DatabaseModel, Group, GroupEvent, GroupEventType, GroupMember, GroupMemberState, MoteEventType, MotesPaths, User } from '../models';
import { QueryFn } from './database';
import { DatabaseService } from './database.service';
import { UserService } from './user.service';
import { MoteEventService } from './mote-event.service';
import { AutoSub, AutoSubOnDestroy } from './auto-sub';
import { UserStateService } from './user-state.service';

/**
 * Provides functionality related to groups, their members and events
 */
@AutoSubOnDestroy()
@Injectable({ providedIn: 'root' })
export class GroupService {
    /** The latest version of the current user's group from the database combined with local group member state */
    private currentGroup: Group = null;
    /** The latest version of the current group member (with local changes). Value exposed in `currentMember` */
    private currentMember$: BehaviorSubject<GroupMember> = new BehaviorSubject<GroupMember>(null);
    /** The latest version of the current group member (with local changes) */
    public currentMember: GroupMember = null;
    /** Disable group behavior on default (e.g. for home sneak-peek audio playing) */
    public isGroupPlayEnabled: boolean = false;

    constructor(private db: DatabaseService, private userService: UserService, private eventService: MoteEventService, private userStateService: UserStateService) {
        if(typeof window === 'undefined') return;
        // Expose GroupService for global usage (i.e. Cypress testing)
        if((window as any)?.Cypress) {
            (window as any).GroupService = this;
        }
    }

    /**
     * Initialize `GroupService`
     */
    public async init(isGroupPlayEnabled: boolean = true) {
        this.isGroupPlayEnabled = isGroupPlayEnabled;
        // Get (and await) the latest version of the current user's group from the database
        this.currentGroup = await this.watchDbGroup().pipe(first()).toPromise();
        // Watch the latest version of the current user's group from the database combined with local group member state
        AutoSub(this).reg['currentGroup'] = this.watchCurrentGroup().subscribe((group) => this.currentGroup = group);

        // Get (and await) the current group member from the database and store value locally
        const currentMember = await this.getDbMember();
        // Watch the latest version of the current group member and store it in a member variable for easy access in other component templates
        AutoSub(this).reg['currentMember'] = this.watchCurrentMember().subscribe((member) => this.currentMember = member);

        this.currentMember$.next(currentMember);

        // Watch the group member in the database and update the local `currentMember$` subject if the user key changes (i.e. in case the user logs in or out)
        AutoSub(this).reg['memberChange'] = this.watchDbMember().pipe(
            filter((member) => member?.key != this.getCurrentMember()?.key))
            .subscribe((member) => this.currentMember$.next(member));

        // If the current group member's state is anything but `IDLE`, set it to `IDLE`. This makes it possible to reset state upon reload.
        if(currentMember && currentMember.state != GroupMemberState.IDLE)
            this.setCurrentMemberState(this.currentGroup.key, GroupMemberState.IDLE);

        // Invoke handler function for mote events
        this.handleMoteEvents();
        // Invoke handler function for user updates
        this.handleUserUpdates();
    }

    /**
     * Create a new group
     * @returns A `Promise` resolving with the new `Group` once the update on the server is complete
     */
    public async createGroup(): Promise<Group> {
        // create a new group
        const group = new Group();
        // add timestamp
        group.createdOn = this.db.serverTime();
        // save group in DB
        const dbPath = MotesPaths.GROUP_DB_PATH(group.key);
        await this.db.database.ref(dbPath).set(group.toJson());
        // return preliminary group (without joinId  and createdOn timestamp)
        return group;
    }

    /**
     * Create a `GroupMember` object for a user
     * @param userKey Key for the user
     */
    public async createMember(userKey: string): Promise<GroupMember> {
        // get the user
        const user = await this.userService.watchUser(userKey, false).pipe(take(1)).toPromise();

        // create a `GroupMember` object
        const groupMember = new GroupMember(null, userKey);
        groupMember.userName = user.userName;
        groupMember.profilePic = user.profilePic;

        return groupMember;
    }

    /**
     * Add a member to a group
     * @param keyOrJoinId Key or Join ID for the group
     * @param member The `GroupMember` to add
     * @returns A `Promise` which resolves once the update on the server is complete
     */
    public async addMember(keyOrJoinId: string | number, member: GroupMember): Promise<boolean> {
        // check the input type of `keyOrJoinId` and get the group key
        const groupKey = await this.getGroupKey(keyOrJoinId);

        // save group key on user in the database
        await this.saveUserGroupKey(member.key, groupKey);

        // Apply the updates
        const dbPath = MotesPaths.GROUP_MEMBER_DB_PATH(groupKey, member.key);
        return this.db.database.ref(dbPath).set(member.toJson()).then(() => true).catch(() => false);
    }

    /**
     * Save group key on `User` in DB. (This write operation is possible because users can only be added by themselves or super admins)
     * @param userKey key for the `User`
     * @param groupKey key for the `Group`
     * @returns A `Promise` which resolves once the update on the server is complete
     */
    private async saveUserGroupKey(userKey: string, groupKey: string): Promise<any> {
        const dbPath = `${MotesPaths.USER_PRIVATE_DB_PATH(userKey)}/groupKey`;
        return this.db.database.ref(dbPath).set(groupKey);
    }

    /**
     * Remove a member from a group
     * @param groupKey Key for the group
     * @param userKey User key for the member
     * @returns A `Promise` which resolves once the update on the server is complete
     */
    public async removeMember(groupKey: string, userKey: string): Promise<any> {
        // Remove the entry for the specified user in the members object
        const dbPath = MotesPaths.GROUP_MEMBER_DB_PATH(groupKey, userKey);
        return this.db.database.ref(dbPath).remove();
        // The group key on the user is removed by a cloud function, deleteGroupKeyOnUser()
    }

    /**
     * Watch the current group member object in the database
     * @returns An `Observable` with the latest updates from the database, or `null` if the current user is not part of a group
     */
    private watchDbMember(): Observable<GroupMember> {
        let userKey = null;

        return this.userService.watchCurrentUser().pipe(
            // store user key for looking up the group member later on
            tap((user) => userKey = user?.key || ''),
            // Get the user's `groupKey` property
            map((user) => user?.groupKey),
            distinctUntilChanged(),
            // if a group key is present on the current user, watch the group. Otherwise, return Observable of null
            switchMap((groupKey) => groupKey ? this.watchGroupByKey(groupKey) : of(null)),
            // look up the group member with the matching user key. If no group, return `null`
            switchMap((group) => of(group?.members[userKey] || null)),
            // share replay of latest value
            shareReplay(1)
        );
    }

    /**
     * Watch latest version of the current group member (with local changes)
     * @returns An Observable with the latest version of the current group member, or `null` if the current user is not part of a group
     */
    public watchCurrentMember(): Observable<GroupMember> {
        return this.currentMember$.asObservable().pipe(shareReplay(1));
    }

    /**
     * Get a copy of the local `GroupMember` object associated with the current user.
     * @returns The `GroupMember` object, if it exists locally. Otherwise, `null`
     */
    public getCurrentMember(): GroupMember {
        // Get the local version of the current member and copy it
        const currentMember = this.currentMember$.value?.copy();
        // If this fails, return null
        if(!currentMember) {
            return null;
        }
        // Return the `GroupMember` object
        return currentMember;
    }

    /**
     * Get a copy of the `GroupMember` object associated with the current user in the database
     * @returns The `GroupMember` object, if it exists in the database. Otherwise, `null`
     */
    public async getDbMember(): Promise<GroupMember> {
        // Get the database version of the current member
        const currentMember = await this.watchDbMember().pipe(first()).toPromise();
        // If this fails, return null
        if(!currentMember) {
            // console.warn('A GroupMember object associated with the current user could not be found in the database');
            return null;
        }
        // Return the `GroupMember` object
        return currentMember;
    }

    /**
     * Allows the current user to join a specified group
     * @param keyOrJoinId key or join ID for the `Group`
     * @returns A `Promise` which resolves once the update on the server is complete.
     */
    public async joinGroup(keyOrJoinId: string | number): Promise<any> | null {
        // get the current user
        const user = await this.userService.watchCurrentUser().pipe(take(1)).toPromise();
        if(!user) return null;

        // check the input type of `keyOrJoinId` and get the group key
        const groupKey = await this.getGroupKey(keyOrJoinId);
        if(!groupKey) return null;

        // create a `GroupMember` object for the current user
        const groupMember = await this.createMember(user.key);

        // Save the new `GroupMember` object locally
        this.currentMember$.next(groupMember);

        // Apply the updates in the database
        await this.addMember(groupKey, groupMember);

        // Add a `GROUP_MEMBER_JOIN` event in the group
        const joinEvent = new GroupEvent();
        joinEvent.userKey = user.key;
        joinEvent.type = GroupEventType.GROUP_MEMBER_JOIN;
        this.saveGroupEvent(groupKey, joinEvent);

        // If this is the first group member, check if there is an active route which should be started in the group
        const group = await this.getGroupByKey(groupKey);
        const activeTour = this.userStateService.getStoredActiveTour();
        if(Object.keys(group.members).length <= 1 && activeTour.tourKey && activeTour.tourKey != MotesPaths.DEFAULT_TOUR_KEY) {
            // Add previewKey if there is one, to grand access to other group members
            this.startTour(user.groupKey, user.key, activeTour.tourKey, activeTour.previewKey);
        }
    }

    /**
     * Allows the current user to leave it's current group.
     * @returns A `Promise` which resolves once the update on the server is complete.
     */
    public async leaveGroup(): Promise<any> | null {
        // Get the current user
        let user = await this.userService.watchCurrentUser().pipe(take(1)).toPromise();
        if(!(user && user.groupKey)) return null;

        // Create GROUP_MEMBER_LEAVE event
        const groupEvent = new GroupEvent();
        groupEvent.userKey = user.key;
        groupEvent.memberKey = user.key;
        groupEvent.type = GroupEventType.GROUP_MEMBER_LEAVE;
        // Save the event to the database
        await this.saveGroupEvent(user.groupKey, groupEvent);

        // Reset group key on the user. Do this to ensure the change is reflected for the user before being reflected for the rest of the group
        await this.saveUserGroupKey(user.key, '');
        // Remove the user from the current group. Database rules should allow users to write to a group as long as their user key is included in the group's members object
        return this.removeMember(user.groupKey, user.key);
    }

    /**
     * Start a group tour.
     * Saves a `GROUP_START_TOUR` `GroupEvent` to the database.
     * @param groupKey Key for the group
     * @param userKey Key for the user initiating the tour
     * @param moteKey Key for the tour (i.e. `Mote`)
     * @returns A `Promise` which resolves once the update on the server is complete.
     */
    public async startTour(groupKey: string, userKey: string, moteKey: string, previewKey: string): Promise<void> {
        // Create GROUP_START_TOUR event
        const groupEvent = new GroupEvent();
        groupEvent.moteKey = moteKey;
        groupEvent.previewKey = previewKey;
        groupEvent.userKey = userKey;
        groupEvent.type = GroupEventType.GROUP_START_TOUR;
        // Save the event to the database
        return this.saveGroupEvent(groupKey, groupEvent);
    }

    /**
     * Start group media playback.
     * Saves a `GROUP_PLAYBACK_REQUEST` `GroupEvent` to the database.
     * @param user The `User` initiating media playback
     * @param moteKey Key for the `Mote` containing the media
     * @param mediaKey Key for the `Media` object
     * @returns A `Promise` which resolves once the update on the server is complete.
     */
    public async startGroupPlay(user: User, moteKey: string, mediaKey: string): Promise<void> {
        // Create a `GROUP_PLAYBACK_REQUEST` event
        const groupEvent = new GroupEvent();
        groupEvent.moteKey = moteKey;
        groupEvent.mediaKey = mediaKey;
        groupEvent.userKey = user.key;
        groupEvent.type = GroupEventType.GROUP_PLAYBACK_REQUEST;

        // Only allow playback requests if the other group members are available
        if(!this.areOtherMembersAvailable(this.currentGroup)) return this.eventService.emitMoteEvent({ type: MoteEventType.GROUP_PREVENT_REQUEST });

        // Set the state of the current user to WAITING
        await this.setCurrentMemberState(user.groupKey, GroupMemberState.WAITING);
        // And save the event
        return this.saveGroupEvent(user.groupKey, groupEvent);
    }

    /**
     * Watch the latest version of the current user's group from the database
     * @returns An Observable with updates from the group
     */
    private watchDbGroup(): Observable<Group> {
        return this.userService.watchCurrentUser().pipe(
            // Get the user's `groupKey` property
            map((user) => user?.groupKey),
            distinctUntilChanged(),
            // if a group key is present on the current user, watch the group. Otherwise, return Observable of null
            switchMap((groupKey) => groupKey ? this.watchGroupByKey(groupKey) : of(null)),
            // share replay of latest value
            shareReplay(1)
        );
    }

    /**
     * Watch the group of the currently signed in `User`
     * Combines the latest version of the user's group from the database with local group member state
     * @returns An Observable with updates from the group
     */
    public watchCurrentGroup(): Observable<Group> {
        return combineLatest([this.watchDbGroup(), this.currentMember$]).pipe(
            switchMap(([dbGroup, currentMember]) => {
                // If no group is present in the database, return null
                if(!dbGroup) return of(null);
                // If present, replace the value of the currentMember in the `Group` object from the database with the local version
                if(currentMember) {
                    dbGroup.members[currentMember.key] = currentMember.copy();
                }
                return of(dbGroup);
            }),
            // share replay of latest value
            shareReplay(1)
        );
    }

    /**
     * Get the group of the currently signed in `User`
     * @returns A `Promise<Group>` with the latest data from the DB
     */
    public getCurrentGroup(): Promise<Group> {
        return this.watchCurrentGroup().pipe(first()).toPromise();
    }

    /**
     * Watch a group with a specified group key
     * @param key Key for the group
     * @returns An `Observable<Group>`
     */
    public watchGroupByKey(key: string): Observable<Group> {
        const dbPath = MotesPaths.GROUP_DB_PATH(key);

        return this.db.object(dbPath).valueChanges().pipe(
            // Construct a new Group from the returned JSON. If no group is found, return null // ? Necessary to check json.length?
            map((json) => json ? new Group().fromJson(json) : null),
            catchError(() => of(null))
        );
    }

    /**
     * Get a group with a specified group key
     * @param groupKey Key for the group
     * @returns A `Promise` which resolves to a `Group`
     */
    public getGroupByKey(groupKey: string): Promise<Group> {
        return this.watchGroupByKey(groupKey).pipe(first()).toPromise();
    }

    /**
     * Save a `GroupEvent` to the database
     * @param groupKey Key for the group
     * @param event The `GroupEvent` which should be saved
     * @returns A `Promise` which resolves once the update on the server is complete.
     */
    public saveGroupEvent(groupKey: string, event: GroupEvent): Promise<any> {
        if(!event.createdOn) event.createdOn = this.db.serverTime();

        const dbPath = MotesPaths.GROUP_EVENTS_DB_PATH(groupKey, event.key);
        return this.db.database.ref(dbPath).set(event.toJson());
    }

    // TODO: Figure out why this fires multiple times for each event notification
    /**
     * Watch events for a specified group
     * @param groupKey Key for the group
     * @returns An `Observable<GroupEvent>`
     */
    public watchGroupEvents(groupKey: string): Observable<GroupEvent> {
        // Order events based on time of creation
        // const query = (ref: DatabaseQuery) => ref.orderByChild('createdOn').limitToLast(1); // ! limitToLast currently not working
        const query: QueryFn = (ref) => ref.orderByChild('createdOn');
        const dbPath = MotesPaths.GROUP_EVENTS_DB_PATH(groupKey);

        const watchEvents = this.db.list<GroupEvent>(dbPath, query);

        return watchEvents.snapshotChanges().pipe(
            // Filter out anything but snapshots with type == 'child_added'
            map((dataSnapshots) => dataSnapshots.filter((snapshot) => snapshot.type == 'child_added')),
            // Filter out empty arrays
            filter((dataSnapshots) => dataSnapshots.length > 0),
            // Construct new GroupEvent
            map((snapshot) => new GroupEvent().fromJson(snapshot[0].payload.val())),
            catchError((error) => of(null))
        );
    }

    /**
     * Get all events for a specified group (from current time to group creation – with newest event first)
     * @param groupKey Key for the group
     * @returns A `Promise` which resolves to `GroupEvent[]`
     */
    public getGroupEvents(groupKey: string): Promise<GroupEvent[]> {
        // Order events based on time of creation
        const query: QueryFn = (ref) => ref.orderByChild('createdOn');
        const dbPath = MotesPaths.GROUP_EVENTS_DB_PATH(groupKey);

        const dbGroupEvents = this.db.list<GroupEvent>(dbPath, query);

        return dbGroupEvents.valueChanges().pipe(
            take(1),
            // Map the json to GroupEvent[] and reverse the array to get most recent events first
            map((groupEvents: GroupEvent[]) => groupEvents ? groupEvents.map((event) => new GroupEvent().fromJson(event)).reverse() : []))
            .toPromise();
    }

    /**
     * Get the latest event from a group
     * @param groupKey Key for the group
     * @param type Defines a specific `GroupEventType` to look up
     * @param moteKey Defines a specific `Mote` key to look up
     * @returns The latest `GroupEvent` with the specified properties. If `type` and `moteKey` is not provided, the latest event regardless of properties. If no event is found, `null`.
     */
    public async getGroupEvent(groupKey: string, type?: GroupEventType, moteKey?: string): Promise<GroupEvent> {
        // Get all group events
        const events = await this.getGroupEvents(groupKey);
        // If no events, return `null`
        if(events.length < 1) return null;
        // If no `type` or `moteKey` parameter is provided, simply return the latest event (or null if there are no events)
        if(!(type || moteKey)) return events[0] ? events[0] : null;
        // Otherwise, find the latest event with the specified properties
        let latestEvent;
        if(type && moteKey) latestEvent = events.find((event) => event.type == type && event.moteKey == moteKey);
        if(type && !moteKey) latestEvent = events.find((event) => event.type == type);
        if(!type && moteKey) latestEvent = events.find((event) => event.moteKey == moteKey);
        // Return the event or null if no event with the specified type was found
        return latestEvent ? latestEvent : null;
    }

    /**
     * Get the key for the active group tour, if any
     * @param groupKey Key for the group
     * @returns A `Promise` which resolves to a tour (mote) key
     */
    public async getActiveTour(groupKey: string): Promise<string> {
        // Get all group events
        const events = await this.getGroupEvents(groupKey);
        // Find the latest GROUP_START_TOUR event and get the associated mote key
        const activeTourKey = events.find((event) => event.type == GroupEventType.GROUP_START_TOUR)?.moteKey;
        // Return the key
        return activeTourKey ? activeTourKey : null;
    }

    /**
     * Set the `state` property of a particular `GroupMember` in a particular `Group`
     * @param groupKey Key for the group
     * @param userKey User key of the group member
     * @param state The `GroupMemberState` to set
     * @returns A `Promise` which resolves once the update on the server is complete.
     */
    public setMemberState(groupKey: string, userKey: string, state: GroupMemberState): Promise<any> {
        const dbPath = MotesPaths.GROUP_MEMBER_STATE_DB_PATH(groupKey, userKey);
        return this.db.database.ref(dbPath).set(state);
    }

    /**
     * Set the state for the `GroupMember` object associated with the current user
     * @param groupKey Key for the group
     * @param state The `GroupMemberState` to set
     * @returns A `Promise` which resolves once the update on the server is complete.
     */
    public setCurrentMemberState(groupKey: string, state: GroupMemberState): Promise<any> {
        // Get the current member
        let currentMember = this.getCurrentMember();
        if(!currentMember) {
            console.warn('You are trying to update properties of a non-existing group member.');
            return;
        }

        // Prevent the same state from being set repeatedly
        if(currentMember.state == state) return;

        // Update the state of the current group member locally
        currentMember.state = state;
        // And notify subscribers
        this.currentMember$.next(currentMember);

        // Update the database
        return this.setMemberState(groupKey, currentMember.key, state);
    }

    /**
     * Set the challenge state for the `GroupMember` object associated with the current user
     * @param groupKey Key for the group
     * @param challengeKey Key for the challenge
     * @param state The `ChallengeState` to set
     * @returns A `Promise` which resolves once the update on the server is complete.
     */
    public setCurrentMemberChallengeState(groupKey: string, challengeKey: string, state: ChallengeState): Promise<any> {
        // Get the current member
        let currentMember = this.getCurrentMember();
        if(!currentMember) {
            console.warn('You are trying to update properties of a non-existing group member.');
            return;
        }

        // Update the state of the current group member locally
        currentMember.challengeStateMap.states[challengeKey] = state;
        // And notify subscribers
        this.currentMember$.next(currentMember);

        // Update the database
        const dbPath = MotesPaths.GROUP_MEMBER_CHALLENGE_STATE_DB_PATH(groupKey, currentMember.key, challengeKey);
        return this.db.database.ref(dbPath).set(state.toJson());
    }

    /**
     * Get the key for a group based on a group key or join ID. Can be used in cases where there is doubt regarding the input type.
     * @param keyOrJoinId Group key or join ID
     * @returns A `Promise` which resolves to the group key
     */
    public async getGroupKey(keyOrJoinId: string | number): Promise<string> {
        let groupKey: string;
        // if the parameter is of type 'number', assume a joinId was provided: Get the group and the key
        if(typeof keyOrJoinId == 'number') {
            groupKey = await this.getKeyFromJoinId(keyOrJoinId);
        } else {
            // else, the key was provided
            groupKey = keyOrJoinId;
        }
        return groupKey;
    }

    /**
     * Get the key for a group associated with a specified join ID
     * @param joinId Join ID for the group
     * @returns A `Promise` which resolves to a group key of type `string`
     */
    private getKeyFromJoinId(joinId: number): Promise<string> {
        const query = ref => (ref.orderByChild('joinId').equalTo(joinId)).limitToFirst(1);
        const dbPath = MotesPaths.GROUP_DB_PATH();

        const groupKeyObservable = this.db.list(dbPath, query).valueChanges().pipe(
            // Construct a new Group from the first entry in the returned JSON array. If no group is found, return null
            map((json) => json.length > 0 ? new Group().fromJson(json[0]) : null),
            // Get the group key
            map((group) => group?.key),
            first(),
            catchError(() => of(null)));

        return groupKeyObservable.toPromise();
    }

    /**
     * Check whether the group member associated with the current user is available for a group activity
     * @returns `true` if the group member associated with the current user is available for a group activity. Otherwise, `false`.
     */
    public isCurrentMemberAvailable(): boolean {
        // Get the current group member
        const currentMember = this.getCurrentMember();
        // If the current member is null or undefined, return `true`
        if(!currentMember) {
            console.warn(`Cannot check state. Current member is ${currentMember}`);
            return true;
        }

        // Check if the current member has a `state` of IDLE or COMPLETING
        const availableStates = [GroupMemberState.IDLE, GroupMemberState.COMPLETING];
        return availableStates.includes(currentMember.state);
    }

    /**
     * Check whether all group members, except the one associated with the current user, are available for a group activity
     * @param group A `Group` object
     * @returns `true` if all group members, except the one associated with the current user, are available for a group activity (and also in the case of a falsy `group` value or no other group members). Otherwise, `false`.
     */
    private areOtherMembersAvailable(group: Group): boolean {
        // If the group is null or undefined, return `true`
        if(!group) {
            console.warn('Cannot check `state` of group members: No group available');
            return true;
        }

        // Get the other group members (i.e. the one associated with the current user is filtered out)
        const currentMember = this.getCurrentMember();
        const otherGroupMembers = DatabaseModel.mapToArray<GroupMember>(group.members).filter((member) => member.key != currentMember?.key);
        // If the group has no other members, return `true`
        if(!otherGroupMembers) return true;

        // Check if a member with a different `state` than IDLE or COMPLETING exists
        const availableStates = [GroupMemberState.IDLE, GroupMemberState.COMPLETING];
        return !otherGroupMembers.find((member) => !availableStates.includes(member.state));
    }

    /**
     * Observe `MoteEvent`s and convert to corresponding `GroupEvent`s
     */
    private handleMoteEvents(): void {
        AutoSub(this).reg['moteEvents'] = this.eventService.watchMoteEvents().pipe(
            withLatestFrom(this.userService.watchCurrentUser()))
            .subscribe(async ([event, user]) => {
                // Do not push any group events if there is no current user, or the current user is not part of a group
                if(!user || !user.groupKey) return;

                // For state events, no group event should be saved. Instead, update the `state` of the group member
                switch(event.type) {
                    case MoteEventType.QUIZ_COMPLETED:
                        this.setCurrentMemberState(user.groupKey, GroupMemberState.COMPLETING);
                        return;
                    case MoteEventType.QUIZ_SKIPPED:
                    case MoteEventType.QUIZ_SCOREBOARD_CLOSED:
                        this.setCurrentMemberState(user.groupKey, GroupMemberState.IDLE);
                        return;
                }

                // Create GroupEvent and populate generic data
                let groupEvent = new GroupEvent();
                // Set mote key (not every MoteEvent has a mote key)
                groupEvent.moteKey = event.mote?.key;
                groupEvent.userKey = user.key;

                // Set GroupEventType based on incoming event
                switch(event.type) {
                    case MoteEventType.START_TOUR:
                        groupEvent.type = GroupEventType.GROUP_START_TOUR;
                        break;
                    case MoteEventType.UNLOCK_MOTE:
                        groupEvent.type = GroupEventType.GROUP_UNLOCK_MOTE;
                        break;
                    case MoteEventType.QUIZ_REQUESTED:
                        // Only allow quiz requests if all group members are available
                        if(!this.areOtherMembersAvailable(this.currentGroup)) return this.eventService.emitMoteEvent({ type: MoteEventType.GROUP_PREVENT_REQUEST });

                        // Set the state of the current user to WAITING
                        await this.setCurrentMemberState(user.groupKey, GroupMemberState.WAITING);
                        // And save the event
                        groupEvent.type = GroupEventType.GROUP_QUIZ_REQUEST;
                        break;
                }

                // If a type was set, save the GroupEvent in the database
                if(groupEvent.type) this.saveGroupEvent(user.groupKey, groupEvent);
            });
    }

    /**
     * Watch updates to the current `User` and update related `GroupMember` properties accordingly
     */
    private handleUserUpdates(): void {
        AutoSub(this).reg['userDiff'] = this.userService.watchCurrentUserDiff().subscribe((userDiffObj) => {
            // If there is no user, or the user has no group key, do nothing
            if(!(userDiffObj.user && userDiffObj.user.groupKey)) return;

            // Otherwise, get the current member
            let currentMember = this.getCurrentMember();
            // If no current member exists, do nothing
            if(!currentMember) return;

            // Check for updates related to `GroupMember` properties by iterating over entries in `Difference[]`
            let profilePicUpdated = false;

            for(const entry of userDiffObj.diff) {
                const path = entry.path[0];

                // Check for updates to `userName`
                if(path == 'userName') {
                    // Update the user name of the current group member locally
                    currentMember.userName = entry.value;
                    // And notify subscribers
                    this.currentMember$.next(currentMember);
                    // Update the database
                    const dbPath = `${MotesPaths.GROUP_MEMBER_DB_PATH(userDiffObj.user.groupKey, userDiffObj.user.key)}/userName`;
                    this.db.database.ref(dbPath).set(entry.value);
                }

                // Check for updates to `profilePic`
                if(path == 'profilePic' && !profilePicUpdated) {
                    // Update the profile picture of the current group member locally
                    currentMember.profilePic = userDiffObj.user.profilePic;
                    // And notify subscribers
                    this.currentMember$.next(currentMember);
                    // Update the database
                    const dbPath = `${MotesPaths.GROUP_MEMBER_DB_PATH(userDiffObj.user.groupKey, userDiffObj.user.key)}/profilePic`;
                    this.db.database.ref(dbPath).update(userDiffObj.user.profilePic.toJson());
                    profilePicUpdated = true;
                }
            }
        });
    }
}
